import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { leaderboardAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import SubscriptionGate from '../../components/SubscriptionGate';
import { motion } from 'framer-motion';
import { Trophy, Flame, Medal, Crown, Star, ChevronRight } from 'lucide-react';

const AGE_GROUPS = ['All', '10U', '12U', '14U', '18U'];
const GENDERS = [
  { label: 'All', value: null },
  { label: 'Boys', value: 'boy' },
  { label: 'Girls', value: 'girl' },
];
const TABS = ['Top Scores', 'Weekly Drills', 'Streaks'];

export default function Leaderboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [ageGroup, setAgeGroup] = useState('All');
  const [gender, setGender] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [tab, ageGroup, gender]);

  const load = async () => {
    setLoading(true);
    try {
      const ageFilter = ageGroup === 'All' ? null : ageGroup;
      let res;
      if (tab === 0) res = await leaderboardAPI.scores(ageFilter, gender);
      else if (tab === 1) res = await leaderboardAPI.weekly(ageFilter, gender);
      else res = await leaderboardAPI.streaks(ageFilter, gender);
      setData(res.data || []);
    } catch { setData([]); }
    setLoading(false);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown size={16} className="text-yellow-400" />;
    if (rank === 2) return <Medal size={16} className="text-gray-300" />;
    if (rank === 3) return <Medal size={16} className="text-amber-600" />;
    return <span className="text-[10px] text-[#64748B] font-bold w-4 text-center">{rank}</span>;
  };

  function getScoreColor(pct) {
    if (pct >= 90) return '#10B981';
    if (pct >= 70) return '#2563EB';
    if (pct >= 50) return '#FF9100';
    return '#EF4444';
  }

  return (
    <Layout title="COMMUNITY">
      <SubscriptionGate>
        <div className="px-4 py-6 space-y-4">
          {/* Header */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight flex items-center gap-2" data-testid="leaderboard-title">
              <Trophy size={20} className="text-[#FF9100]" /> LEADERBOARD
            </h2>
          </div>

          {/* Tab Toggle */}
          <div className="flex gap-1.5">
            {TABS.map((t, i) => (
              <button
                key={t}
                onClick={() => setTab(i)}
                className={`flex-1 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                  tab === i
                    ? 'bg-[#2563EB] text-white'
                    : 'bg-white border border-[#E2E8F0] text-[#64748B]'
                }`}
                data-testid={`tab-${t.toLowerCase().replace(/\s/g, '-')}`}
              >{t}</button>
            ))}
          </div>

          {/* Gender Filter (All / Boys / Girls) */}
          <div className="flex gap-1.5">
            {GENDERS.map(g => (
              <button
                key={g.label}
                onClick={() => setGender(g.value)}
                className={`flex-1 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
                  gender === g.value
                    ? 'bg-[#F472B6]/15 text-[#F472B6] border border-[#F472B6]/40'
                    : 'bg-white border border-[#E2E8F0] text-[#64748B]'
                }`}
                data-testid={`gender-filter-${g.label.toLowerCase()}`}
              >{g.label}</button>
            ))}
          </div>

          {/* Age Group Filter */}
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {AGE_GROUPS.map(ag => (
              <button
                key={ag}
                onClick={() => setAgeGroup(ag)}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider whitespace-nowrap transition-all ${
                  ageGroup === ag
                    ? 'bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/30'
                    : 'bg-white border border-[#E2E8F0] text-[#64748B]'
                }`}
                data-testid={`age-filter-${ag.toLowerCase()}`}
              >{ag}</button>
            ))}
          </div>

          {/* List */}
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner" /></div>
          ) : data.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
              <Trophy size={32} className="mx-auto text-[#1C2B54] mb-3" />
              <p className="text-[#64748B] text-sm">No rankings yet. Complete drills to get on the board!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.map((entry, i) => (
                <motion.div
                  key={entry.user_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => navigate(`/profile/${entry.user_id}`)}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer active:scale-[0.98] ${
                    entry.is_you
                      ? 'bg-[#2563EB]/5 border border-[#2563EB]/20'
                      : 'bg-white border border-[#E2E8F0] hover:border-[#2563EB]/40'
                  }`}
                  data-testid={`leaderboard-entry-${i}`}
                >
                  {/* Rank */}
                  <div className="w-8 flex justify-center">{getRankIcon(entry.rank)}</div>

                  {/* Player Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-['Barlow_Condensed'] text-sm font-bold truncate">
                        {entry.display_name}
                      </span>
                      {entry.is_you && (
                        <span className="text-[8px] bg-[#2563EB] text-white px-1.5 py-0.5 rounded-full font-bold">YOU</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {entry.position && (
                        <span className="text-[9px] text-[#00E5FF] font-bold uppercase">{entry.position}</span>
                      )}
                      {entry.age_group && (
                        <span className="text-[9px] text-[#A78BFA] font-bold">{entry.age_group}</span>
                      )}
                    </div>
                  </div>

                  {/* Stat */}
                  <div className="text-right flex items-center gap-2">
                    {tab === 0 ? (
                      <div>
                        <div className="flex items-center gap-1 justify-end">
                          <Star size={12} style={{ color: getScoreColor(entry.weekly_avg_score) }} />
                          <span className="font-['Barlow_Condensed'] text-xl font-bold" style={{ color: getScoreColor(entry.weekly_avg_score) }}>
                            {entry.weekly_avg_score}
                          </span>
                        </div>
                        {entry.weekly_bonus > 0 ? (
                          <p className="text-[8px] text-[#FF9100] font-bold uppercase">+{entry.weekly_bonus} wk bonus</p>
                        ) : (
                          <p className="text-[8px] text-[#64748B] uppercase">{entry.days_completed}d avg</p>
                        )}
                      </div>
                    ) : tab === 1 ? (
                      <div>
                        <span className="font-['Barlow_Condensed'] text-xl font-bold text-[#2563EB]">{entry.drills_completed}</span>
                        <p className="text-[8px] text-[#64748B] uppercase">drills</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Flame size={14} className="text-[#FF9100]" />
                        <span className="font-['Barlow_Condensed'] text-xl font-bold text-[#FF9100]">{entry.current_streak}</span>
                      </div>
                    )}
                    <ChevronRight size={14} className="text-[#64748B] ml-1 flex-shrink-0" />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </SubscriptionGate>
    </Layout>
  );
}
